import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { backupConfigAPI } from '../api/backupConfig';
import { getAllScheduleMasters, getAllSftpServerMasters } from '../services/api';
import { toast } from 'react-toastify';
import './CreateBackupConfigTabs.css';

const CreateBackupConfig = () => {
  const navigate = useNavigate();
  const { uniqueId } = useParams();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [sftpServers, setSftpServers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [usePresetSchedule, setUsePresetSchedule] = useState(true);
  const [presetSchedule, setPresetSchedule] = useState('daily-1x');
  
  // New state for schedule-SFTP mapping
  const [scheduleSftpMapping, setScheduleSftpMapping] = useState({});

  const [formData, setFormData] = useState({
    configName: '',
    description: '',
    sqlServerConfig: {
      host: '',
      port: 1433,
      username: '',
      password: '',
      database: '',
      instanceName: ''
    },
    backupPath: '/backups',
    schedules: [],
    sftpServers: [],
    isActive: true
  });

  // Preset schedule options
  const presetSchedules = [
    { id: 'hourly', name: 'Every Hour', description: 'Backup every hour (24 times per day)', icon: '⏰' },
    { id: 'daily-1x', name: 'Daily 1 Time', description: 'Backup once daily at 2:00 AM', icon: '🌙' },
    { id: 'daily-2x', name: 'Daily 2 Times', description: 'Backup twice daily (2:00 AM & 2:00 PM)', icon: '☀️' },
    { id: 'daily-3x', name: 'Daily 3 Times', description: 'Backup 3 times daily (6:00 AM, 2:00 PM, 10:00 PM)', icon: '🔄' }
  ];

  useEffect(() => {
    fetchMasters();
  }, []);

  const fetchMasters = async () => {
    try {
      const [schedulesRes, serversRes] = await Promise.all([
        getAllScheduleMasters(),
        getAllSftpServerMasters()
      ]);

      setSchedules(schedulesRes.data.filter(s => s.isActive));
      setSftpServers(serversRes.data.filter(s => s.isActive));
    } catch (error) {
      toast.error('Failed to fetch master data');
    }
  };

  const handleChange = (e, section, field) => {
    const { value, type, checked } = e.target;

    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: type === 'number' ? Number(value) : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleScheduleToggle = (scheduleId) => {
    setFormData(prev => {
      const schedules = prev.schedules.includes(scheduleId)
        ? prev.schedules.filter(id => id !== scheduleId)
        : [...prev.schedules, scheduleId];
      return { ...prev, schedules };
    });

    // Initialize mapping for new schedule with all SFTP servers
    if (!formData.schedules.includes(scheduleId)) {
      setScheduleSftpMapping(prev => ({
        ...prev,
        [scheduleId]: sftpServers.map(s => s._id)
      }));
    } else {
      // Remove mapping when schedule is deselected
      setScheduleSftpMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[scheduleId];
        return newMapping;
      });
    }
  };

  const handleSftpToggle = (serverId) => {
    setFormData(prev => {
      const sftpServers = prev.sftpServers.includes(serverId)
        ? prev.sftpServers.filter(id => id !== serverId)
        : [...prev.sftpServers, serverId];
      return { ...prev, sftpServers };
    });
  };

  // New handler for mapping SFTP servers to specific schedules
  const handleMappingSftpToggle = (scheduleId, sftpId) => {
    setScheduleSftpMapping(prev => {
      const currentMapping = prev[scheduleId] || [];
      const newMapping = currentMapping.includes(sftpId)
        ? currentMapping.filter(id => id !== sftpId)
        : [...currentMapping, sftpId];
      
      return {
        ...prev,
        [scheduleId]: newMapping
      };
    });
  };

  const getSchedulesForPreset = () => {
    switch (presetSchedule) {
      case 'hourly':
        return schedules.filter(s => s.frequency === 'hourly').map(s => s._id);
      case 'daily-1x':
        return schedules.filter(s => s.frequency === 'daily' && s.time === '02:00').slice(0, 1).map(s => s._id);
      case 'daily-2x':
        return schedules.filter(s => s.frequency === 'daily' && (s.time === '02:00' || s.time === '14:00')).slice(0, 2).map(s => s._id);
      case 'daily-3x':
        return schedules.filter(s => s.frequency === 'daily' && ['06:00', '14:00', '22:00'].includes(s.time)).slice(0, 3).map(s => s._id);
      default:
        return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalSchedules = usePresetSchedule ? getSchedulesForPreset() : formData.schedules;

    if (finalSchedules.length === 0) {
      toast.error('Please select at least one schedule');
      return;
    }

    if (formData.sftpServers.length === 0) {
      toast.error('Please select at least one SFTP server');
      return;
    }

    // Validate mapping
    for (const scheduleId of finalSchedules) {
      if (!scheduleSftpMapping[scheduleId] || scheduleSftpMapping[scheduleId].length === 0) {
        toast.error('Please map at least one SFTP server to each schedule');
        return;
      }
    }

    setLoading(true);

    try {
      // Create multiple configurations based on schedule-SFTP mappings
      const configsToCreate = [];
      
      for (const scheduleId of finalSchedules) {
        const mappedSftpServers = scheduleSftpMapping[scheduleId] || [];
        const schedule = schedules.find(s => s._id === scheduleId);
        
        for (const sftpId of mappedSftpServers) {
          const sftpServer = sftpServers.find(s => s._id === sftpId);
          
          const configData = {
            configName: `${formData.configName} - ${schedule?.name || schedule?.time} - ${sftpServer?.serverName}`,
            description: formData.description,
            sqlServerConfig: formData.sqlServerConfig,
            backupPath: formData.backupPath,
            schedules: [scheduleId],
            sftpServers: [sftpId],
            isActive: formData.isActive
          };
          
          configsToCreate.push(configData);
        }
      }

      // Create all configurations
      const createPromises = configsToCreate.map(config => 
        backupConfigAPI.create(uniqueId, config)
      );

      await Promise.all(createPromises);

      toast.success(`Successfully created ${configsToCreate.length} backup configuration(s)`);
      navigate(`/installations/${uniqueId}`);
    } catch (error) {
      toast.error(error.message || 'Failed to create backup configurations');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: '📝' },
    { id: 'sql', label: 'Source Server', icon: '🗄️' },
    { id: 'schedule', label: 'Schedule', icon: '⏰' },
    { id: 'sftp', label: 'SFTP Servers', icon: '🖥️' },
    { id: 'mapping', label: 'Schedule-SFTP Mapping', icon: '🔗' }
  ];

  const isTabValid = (tabId) => {
    switch (tabId) {
      case 'basic':
        return formData.configName.length >= 3;
      case 'sql':
        return formData.sqlServerConfig.host && formData.sqlServerConfig.username &&
               formData.sqlServerConfig.password && formData.sqlServerConfig.database;
      case 'schedule':
        return usePresetSchedule ? true : formData.schedules.length > 0;
      case 'sftp':
        return formData.sftpServers.length > 0;
      case 'mapping':
        const finalSchedules = usePresetSchedule ? getSchedulesForPreset() : formData.schedules;
        return finalSchedules.every(scheduleId => 
          scheduleSftpMapping[scheduleId] && scheduleSftpMapping[scheduleId].length > 0
        );
      default:
        return false;
    }
  };

  const canProceedToNext = () => {
    return isTabValid(activeTab);
  };

  const nextTab = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1 && canProceedToNext()) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  };

  const prevTab = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  return (
    <div className="create-backup-config-page">
      <div className="backup-config-header">
        <h1>🔧 Create Backup Configuration</h1>
        <p>Configure a new backup for this installation</p>
      </div>

      <div className="tabs-container">
        <div className="tabs-navigation">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${isTabValid(tab.id) ? 'valid' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {isTabValid(tab.id) && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="tab-content">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="tab-panel">
              <h2>📝 Basic Information</h2>
              
              <div className="form-group">
                <label htmlFor="configName">Configuration Name *</label>
                <input
                  id="configName"
                  type="text"
                  placeholder="e.g., Production DB Backup"
                  value={formData.configName}
                  onChange={(e) => handleChange(e, null, 'configName')}
                  required
                  minLength="3"
                />
                <small>Enter a descriptive name (minimum 3 characters)</small>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  rows="4"
                  placeholder="Optional description of this backup configuration"
                  value={formData.description}
                  onChange={(e) => handleChange(e, null, 'description')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="backupPath">Backup Path</label>
                <input
                  id="backupPath"
                  type="text"
                  placeholder="/backups"
                  value={formData.backupPath}
                  onChange={(e) => handleChange(e, null, 'backupPath')}
                />
                <small>Server path where backups will be stored</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange(e, null, 'isActive')}
                  />
                  <span>Active Configuration</span>
                </label>
                <small>Enable this backup configuration immediately</small>
              </div>
            </div>
          )}

          {/* Source Server Tab */}
          {activeTab === 'sql' && (
            <div className="tab-panel">
              <h2>🗄️ Source Server Configuration</h2>

              <div className="form-group">
                <label htmlFor="sqlHost">Host *</label>
                <input
                  id="sqlHost"
                  type="text"
                  placeholder="localhost or server IP"
                  value={formData.sqlServerConfig.host}
                  onChange={(e) => handleChange(e, 'sqlServerConfig', 'host')}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="sqlPort">Port</label>
                  <input
                    id="sqlPort"
                    type="number"
                    placeholder="1433"
                    value={formData.sqlServerConfig.port}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'port')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="instanceName">Instance Name</label>
                  <input
                    id="instanceName"
                    type="text"
                    placeholder="MSSQLSERVER"
                    value={formData.sqlServerConfig.instanceName}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'instanceName')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="sqlUsername">Username *</label>
                <input
                  id="sqlUsername"
                  type="text"
                  placeholder="sa or database username"
                  value={formData.sqlServerConfig.username}
                  onChange={(e) => handleChange(e, 'sqlServerConfig', 'username')}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="sqlPassword">Password *</label>
                <div className="password-input-wrapper">
                  <input
                    id="sqlPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={formData.sqlServerConfig.password}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'password')}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="database">Database Name *</label>
                <input
                  id="database"
                  type="text"
                  placeholder="Database to backup"
                  value={formData.sqlServerConfig.database}
                  onChange={(e) => handleChange(e, 'sqlServerConfig', 'database')}
                  required
                />
              </div>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="tab-panel">
              <h2>⏰ Backup Schedule</h2>

              <div className="schedule-mode-selector">
                <button
                  type="button"
                  className={`mode-button ${usePresetSchedule ? 'active' : ''}`}
                  onClick={() => setUsePresetSchedule(true)}
                >
                  📋 Preset Schedules
                </button>
                <button
                  type="button"
                  className={`mode-button ${!usePresetSchedule ? 'active' : ''}`}
                  onClick={() => setUsePresetSchedule(false)}
                >
                  🎯 Custom Selection
                </button>
              </div>

              {usePresetSchedule ? (
                <div className="preset-schedules">
                  {presetSchedules.map(preset => (
                    <div
                      key={preset.id}
                      className={`preset-card ${presetSchedule === preset.id ? 'selected' : ''}`}
                      onClick={() => setPresetSchedule(preset.id)}
                    >
                      <div className="preset-icon">{preset.icon}</div>
                      <h3>{preset.name}</h3>
                      <p>{preset.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="schedules-grid">
                  {schedules.length === 0 ? (
                    <p className="no-data">No active schedules available</p>
                  ) : (
                    schedules.map(schedule => (
                      <div
                        key={schedule._id}
                        className={`schedule-card ${formData.schedules.includes(schedule._id) ? 'selected' : ''}`}
                        onClick={() => handleScheduleToggle(schedule._id)}
                      >
                        <div className="card-header">
                          <span className="schedule-name">{schedule.name}</span>
                          <input
                            type="checkbox"
                            checked={formData.schedules.includes(schedule._id)}
                            onChange={() => {}}
                          />
                        </div>
                        <div className="card-body">
                          <p><strong>Frequency:</strong> {schedule.frequency}</p>
                          {schedule.time && <p><strong>Time:</strong> {schedule.time}</p>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* SFTP Servers Tab */}
          {activeTab === 'sftp' && (
            <div className="tab-panel">
              <h2>🖥️ SFTP Servers</h2>
              <p className="tab-description">Select SFTP servers for backup storage</p>

              <div className="servers-grid">
                {sftpServers.length === 0 ? (
                  <p className="no-data">No active SFTP servers available</p>
                ) : (
                  sftpServers.map(server => (
                    <div
                      key={server._id}
                      className={`server-card ${formData.sftpServers.includes(server._id) ? 'selected' : ''}`}
                      onClick={() => handleSftpToggle(server._id)}
                    >
                      <div className="card-header">
                        <span className="server-name">{server.serverName}</span>
                        <input
                          type="checkbox"
                          checked={formData.sftpServers.includes(server._id)}
                          onChange={() => {}}
                        />
                      </div>
                      <div className="card-body">
                        <p><strong>Host:</strong> {server.host}</p>
                        <p><strong>Port:</strong> {server.port}</p>
                        {server.remotePath && <p><strong>Path:</strong> {server.remotePath}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Mapping Tab */}
          {activeTab === 'mapping' && (
            <div className="tab-panel">
              <h2>🔗 Schedule-SFTP Mapping</h2>
              <p className="tab-description">
                Map each schedule to specific SFTP server(s). Each combination will create a separate backup configuration.
              </p>

              {(usePresetSchedule ? getSchedulesForPreset() : formData.schedules).length === 0 ? (
                <div className="info-box">
                  <p>⚠️ Please select schedules first</p>
                </div>
              ) : (
                <div className="mapping-container">
                  {(usePresetSchedule ? getSchedulesForPreset() : formData.schedules).map(scheduleId => {
                    const schedule = schedules.find(s => s._id === scheduleId);
                    if (!schedule) return null;

                    return (
                      <div key={scheduleId} className="mapping-section">
                        <h3 className="schedule-title">
                          📅 {schedule.name} ({schedule.frequency} {schedule.time ? `at ${schedule.time}` : ''})
                        </h3>
                        
                        <div className="mapping-grid">
                          {sftpServers
                            .filter(server => formData.sftpServers.includes(server._id))
                            .map(server => (
                              <div
                                key={server._id}
                                className={`mapping-card ${
                                  scheduleSftpMapping[scheduleId]?.includes(server._id) ? 'selected' : ''
                                }`}
                                onClick={() => handleMappingSftpToggle(scheduleId, server._id)}
                              >
                                <div className="card-header">
                                  <span className="server-name">{server.serverName}</span>
                                  <input
                                    type="checkbox"
                                    checked={scheduleSftpMapping[scheduleId]?.includes(server._id) || false}
                                    onChange={() => {}}
                                  />
                                </div>
                                <div className="card-body">
                                  <p><strong>Host:</strong> {server.host}:{server.port}</p>
                                  {server.remotePath && <p><strong>Path:</strong> {server.remotePath}</p>}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })}

                  <div className="info-box mapping-summary">
                    <h4>📊 Summary</h4>
                    <p>
                      <strong>Total Configurations to Create:</strong>{' '}
                      {Object.values(scheduleSftpMapping).reduce((sum, servers) => sum + servers.length, 0)}
                    </p>
                    <small>
                      Each schedule-SFTP combination will create a separate backup configuration with auto-generated names.
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="form-actions">
            {tabs.findIndex(t => t.id === activeTab) > 0 && (
              <button type="button" className="btn btn-secondary" onClick={prevTab}>
                ← Previous
              </button>
            )}

            {tabs.findIndex(t => t.id === activeTab) < tabs.length - 1 ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={nextTab}
                disabled={!canProceedToNext()}
              >
                Next →
              </button>
            ) : (
              <button
                type="submit"
                className="btn btn-success"
                disabled={loading || !isTabValid('mapping')}
              >
                {loading ? 'Creating...' : 'Create Configuration(s)'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBackupConfig;